// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import Button from 'shared/components/common/Button';
import GeneratePOsForLowStockMutation from 'main-app/mutations/GeneratePOsForLowStock';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const ShortfallCell = styled.div`
  font-weight: 600;
  color: #c62828;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

class LowStockList extends PureComponent<Props> {
  handleGeneratePOs = (lowStockItems: Array<any>) => {
    if (!lowStockItems || lowStockItems.length === 0) {
      Actions.alertNotification(
        i18n.t('No low stock items to generate POs for'),
        'warning'
      );
      return;
    }

    const confirmMessage = i18n.t(
      `This will generate purchase orders for ${lowStockItems.length} low stock item(s). Continue?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Show loading notification
    Actions.alertNotification(
      i18n.t('Generating purchase orders...'),
      'info'
    );

    GeneratePOsForLowStockMutation.commit(
      {},
      {
        onSuccess: response => {
          const count = response?.generatePOsForLowStock?.purchaseOrders?.length || 0;
          const itemsProcessed = response?.generatePOsForLowStock?.itemsProcessed || 0;
          
          console.log('PO Generation Response:', {
            purchaseOrdersCount: count,
            itemsProcessed: itemsProcessed,
            fullResponse: response
          });
          
          if (count > 0) {
            Actions.alertNotification(
              i18n.t(`Successfully generated ${count} purchase order(s)!`),
              'success'
            );
            
            // Refetch the low stock items query to update the list
            this.props.relay.refetch({}, null, null, { force: true });
          } else {
            Actions.alertNotification(
              i18n.t(`No purchase orders were generated. Items processed: ${itemsProcessed}. Check backend logs and vendor pricing.`),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate purchase orders: ${error.message || 'Unknown error'}`),
            'error'
          );
        },
      }
    );
  };

  render() {
    return (
      <Page>
        <QueryRenderer
          environment={relayEnvironment}
          query={graphql`
            query LowStockListQuery {
              lowStockItems {
                item {
                  id
                  itemNumber
                  name
                }
                onHand
                safetyQuantity
                shortfall
              }
            }
          `}
          render={({ props, error }) => {
            if (error) {
              return (
                <EmptyState>
                  {i18n.t('Error loading low stock items')}
                </EmptyState>
              );
            }

            if (!props) {
              return <Loader />;
            }

            const { lowStockItems } = props;
            const hasLowStock = lowStockItems && lowStockItems.length > 0;

            return (
              <>
                <PageHeaderList
                  title={i18n.t('Low Stock Items')}
                  subtitle={i18n.t('Items below safety quantity threshold')}
                />

                <ContentWrapper>
                  {hasLowStock && (
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        buttonType="primary"
                        onClick={() => this.handleGeneratePOs(lowStockItems)}
                      >
                        {i18n.t('Generate POs')}
                      </Button>
                    </div>
                  )}
                  {!hasLowStock ? (
                    <EmptyState>
                      {i18n.t('No low stock items. All inventory levels are healthy!')}
                    </EmptyState>
                  ) : (
                    <Table
                      columns={[
                        {
                          Header: i18n.t('Item Number'),
                          accessor: 'itemNumber',
                          width: 150,
                        },
                        {
                          Header: i18n.t('Item Name'),
                          accessor: 'itemName',
                          width: 300,
                        },
                        {
                          Header: i18n.t('Current Qty'),
                          accessor: 'onHand',
                          width: 120,
                          headerStyle: { textAlign: 'right' },
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Safety Qty'),
                          accessor: 'safetyQuantity',
                          width: 120,
                          headerStyle: { textAlign: 'right' },
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Shortfall'),
                          accessor: 'shortfall',
                          width: 120,
                          headerStyle: { textAlign: 'right' },
                          style: { textAlign: 'right' },
                        },
                        {
                          Header: i18n.t('Suggested Reorder Qty'),
                          accessor: 'suggestedReorder',
                          width: 150,
                          headerStyle: { textAlign: 'right' },
                          style: { textAlign: 'right' },
                        },
                      ]}
                      data={lowStockItems.map(lowStockItem => ({
                        id: lowStockItem.item.id,
                        itemNumber: lowStockItem.item.itemNumber,
                        itemName: lowStockItem.item.name,
                        onHand: lowStockItem.onHand?.toFixed(2) || '0.00',
                        safetyQuantity: lowStockItem.safetyQuantity?.toFixed(2) || '0.00',
                        shortfall: (
                          <ShortfallCell>
                            {lowStockItem.shortfall?.toFixed(2) || '0.00'}
                          </ShortfallCell>
                        ),
                        suggestedReorder: lowStockItem.shortfall?.toFixed(2) || '0.00',
                      }))}
                    />
                  )}
                </ContentWrapper>
              </>
            );
          }}
        />
      </Page>
    );
  }
}

export default LowStockList;
